import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { X, Crown, Gift, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const UpgradeModal = ({ onClose }) => {
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [billingConfig, setBillingConfig] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPremiumStatus();
    axios.get(`${API}/payments/config`).then((r) => setPaymentMode(r.data.mode)).catch(() => {});
    axios.get(`${API}/subscriptions/config`).then((r) => setBillingConfig(r.data)).catch(() => {});
    axios.get(`${API}/subscriptions/status`).then((r) => setSubscription(r.data)).catch(() => {});
  }, []);

  const loadPremiumStatus = async () => {
    try {
      const response = await axios.get(`${API}/premium/status`);
      setPremiumStatus(response.data);
    } catch (error) {
      console.error('Error loading premium status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReferralCode = () => {
    if (premiumStatus?.referral_code) {
      const referralLink = `${window.location.origin}?ref=${premiumStatus.referral_code}`;
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleUpgrade = async (packageType) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        owner_type: 'individual', plan_key: packageType
      });
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const subscriptionActive = ['active', 'trialing'].includes(subscription?.status);
  const startPortal = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/subscriptions/portal`, { owner_type: 'individual' });
      if (res.data.url) window.location.assign(res.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unable to open billing settings.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <Card className="glass p-8 max-w-2xl w-full">
          <div className="text-white text-center">Loading...</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <Card className="glass p-8 max-w-3xl w-full my-8">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-yellow-400" />
            <h2 className="text-3xl font-bold text-white">Upgrade to Premium</h2>
          </div>
          <Button
            onClick={onClose}
            variant="outline"
            className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold"
            title="Close"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        {/* Beta test-mode notice */}
        {paymentMode === 'test' && (
          <div className="bg-amber-950/40 border border-amber-700 rounded-lg p-3 mb-6" data-testid="test-mode-banner">
            <p className="text-amber-300 text-sm font-semibold">
              🧪 Beta test mode — checkout is fully functional but <span className="underline">no real charges</span> will be made. Use Stripe test card 4242 4242 4242 4242.
            </p>
          </div>
        )}

        {/* Trial Status */}
        {premiumStatus?.trial_active && (
          <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-400 rounded-lg p-4 mb-6">
            <p className="text-emerald-300 font-semibold">
              🎉 You're on a free trial! {premiumStatus.days_remaining} days remaining
            </p>
            <p className="text-emerald-200 text-sm mt-1">
              Upgrade now to continue enjoying premium features after your trial ends.
            </p>
          </div>
        )}

        {/* Premium Features */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-white mb-4">Premium Nutrition Includes:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="glass p-4 rounded-lg">
              <p className="text-white font-semibold">🍎 Full Nutrition Tracking</p>
              <p className="text-blue-300 text-sm">Calories, macros, hydration and custom goals</p>
            </div>
            <div className="glass p-4 rounded-lg">
              <p className="text-white font-semibold">📷 Smart Food Logging</p>
              <p className="text-blue-300 text-sm">USDA search, barcode, meal photo and recipe tools</p>
            </div>
            <div className="glass p-4 rounded-lg">
              <p className="text-white font-semibold">📊 Nutrition Trends & Habits</p>
              <p className="text-blue-300 text-sm">Weekly summaries, streaks and nutrition achievements</p>
            </div>
            <div className="glass p-4 rounded-lg">
              <p className="text-white font-semibold">🥗 Meal Planning Tools</p>
              <p className="text-blue-300 text-sm">Meal templates, leftovers, fasting and meal recommendations</p>
            </div>
          </div>
          <p className="text-stone-400 text-sm mt-4 text-center" data-testid="workouts-stay-free-note">
            Workouts stay free — including personalized workout generation and exercise substitutions.
          </p>
        </div>

        {subscriptionActive && (
          <div className="glass p-4 mb-5 border border-emerald-800 text-emerald-300 text-sm">
            Recurring Premium subscription: {subscription.plan_key}.
            {subscription.cancel_at_period_end ? ' Cancellation scheduled; access remains until the paid-through date.' : ' Automatically renews until cancelled.'}
            <Button onClick={startPortal} className="ml-3 bg-stone-800 text-white">Manage Billing</Button>
          </div>
        )}
        {!subscriptionActive && !billingConfig?.plans?.['individual:monthly'] && !billingConfig?.plans?.['individual:annual'] && (
          <p className="text-amber-300 text-sm mb-4">Subscription checkout is not configured yet. No charges will be initiated.</p>
        )}
        <p className="text-stone-400 text-xs mb-3">Recurring subscription. Automatically renews until cancelled in Manage Billing.</p>
        {/* Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="glass p-6 rounded-xl border-2 border-blue-400">
            <p className="text-blue-300 text-sm mb-2">Monthly Plan</p>
            <p className="text-4xl font-bold text-white mb-1">$7.99</p>
            <p className="text-blue-300 text-sm mb-4">per month</p>
            <Button
              onClick={() => handleUpgrade('monthly')}
              disabled={loading || subscriptionActive || !billingConfig?.plans?.['individual:monthly']}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold py-3 rounded-xl"
            >
              {loading ? 'Processing...' : 'Select Monthly'}
            </Button>
          </div>
          <div className="glass p-6 rounded-xl border-2 border-yellow-400 relative">
            <div className="absolute -top-3 right-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full">
              SAVE $26
            </div>
            <p className="text-yellow-300 text-sm mb-2">Annual Plan</p>
            <p className="text-4xl font-bold text-white mb-1">$69.99</p>
            <p className="text-yellow-300 text-sm mb-4">per year (~$5.83/mo)</p>
            <Button
              onClick={() => handleUpgrade('annual')}
              disabled={loading || subscriptionActive || !billingConfig?.plans?.['individual:annual']}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-semibold py-3 rounded-xl"
            >
              {loading ? 'Processing...' : 'Select Annual'}
            </Button>
          </div>
        </div>

        {/* Referral Section */}
        <div className="glass p-6 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="w-6 h-6 text-purple-400" />
            <h3 className="text-white font-bold">Get Premium Free!</h3>
          </div>
          <p className="text-purple-200 text-sm mb-4">
            Share your referral link and earn <span className="font-bold text-white">7 Premium days</span> for each friend who completes account setup.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={`${window.location.origin}?ref=${premiumStatus?.referral_code}`}
              readOnly
              className="flex-1 glass text-white px-4 py-3 rounded-lg text-sm"
            />
            <Button
              onClick={handleCopyReferralCode}
              className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white px-6 rounded-lg"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
          <p className="text-purple-300 text-xs mt-2">
            {premiumStatus?.referral_count || 0} qualified referral(s) • {premiumStatus?.available_referral_days || 0} Premium days available
          </p>
        </div>

        {/* Maybe Later Button */}
        <div className="mt-6 text-center">
          <Button
            onClick={onClose}
            variant="ghost"
            className="text-stone-400 hover:text-white hover:bg-stone-800 font-semibold px-8 py-2"
          >
            Maybe Later
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default UpgradeModal;
